import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { suggestionsAPI, apiResource } from "../../services/api";
import {
  PartnerInfoWithClientCode,
  AddressesState,
  UpdatePartnerInfoWithClientCode,
} from "../../types";

const addressesAPI = apiResource("addresses");

const initialState: AddressesState = {
  addresses: [],
  currentAddress: null,
  suggestions: [],
  loading: false,
  error: null,
  totalFound: 0,
  searchQuery: "",
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get Address Suggestions
export const getAddressSuggestions = createAsyncThunk(
  "address/getAddressSuggestions",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await suggestionsAPI.getAddresses(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch address suggestions"
      );
    }
  }
);

export const getAddresses = createAsyncThunk(
  "address/getAddresses",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await addressesAPI.getAll(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch address"
      );
    }
  }
);

export const getAddress = createAsyncThunk(
  "address/getAddress",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await addressesAPI.getOne(id);
      return response.data.data.address;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch address"
      );
    }
  }
);

export const createAddress = createAsyncThunk(
  "address/createAddress",
  async (addressData: PartnerInfoWithClientCode, { rejectWithValue }) => {
    try {
      const response = await addressesAPI.create(addressData);
      return response.data.data.address;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create address"
      );
    }
  }
);

export const updateAddress = createAsyncThunk(
  "address/updateAddress",
  async (
    {
      id,
      addressData,
    }: { id: string; addressData: UpdatePartnerInfoWithClientCode },
    { rejectWithValue }
  ) => {
    try {
      const response = await addressesAPI.update(id, addressData);
      return response.data.data.address;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update address"
      );
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "address/deleteAddress",
  async (id: string, { rejectWithValue }) => {
    try {
      await addressesAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete address"
      );
    }
  }
);

const addressesSlice = createSlice({
  name: "addresses",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.totalFound = 0;
      state.searchQuery = "";
    },
    clearCurrentAddress: (state) => {
      state.currentAddress = null;
    },
    resetAddressState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Address Suggestions
      .addCase(getAddressSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAddressSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload.suggestions;
        state.totalFound = action.payload.totalFound;
        state.searchQuery = action.payload.searchQuery;
        state.error = null;
      })
      .addCase(getAddressSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.suggestions = [];
        state.totalFound = 0;
      })
      // Get Addresses
      .addCase(getAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload.addresses;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Address
      .addCase(getAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAddress = action.payload;
        state.error = null;
      })
      .addCase(getAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Address
      .addCase(createAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses.unshift(action.payload);
        state.error = null;
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Address
      .addCase(updateAddress.fulfilled, (state, action) => {
        const index = state.addresses.findIndex(
          (address) => address._id === action.payload._id
        );
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
        if (state.currentAddress?._id === action.payload._id) {
          state.currentAddress = action.payload;
        }
      })
      // Delete Address
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter(
          (address) => address._id !== action.payload
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuggestions, resetAddressState } =
  addressesSlice.actions;
export default addressesSlice.reducer;
